import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedSelector } from '../..'
import { validateDefectInfo } from '../../actions/defectAssistant'
import { DefectAssistantValidateResponse } from '../../actions/defectAssistant/types'
import { color, theme } from '../../Theme'
import { AiButton } from './CtaButtons'
import { CreateDefectFormData } from './types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const getColorForLevel = (level: string): string => {
  switch (level.toLowerCase()) {
    case 'ok':
      return '#2e7d32'
    case 'warning':
      return '#fbc02d'
    case 'error':
      return '#c62828'
    default:
      return '#2e7d32'
  }
}

const getBackgroundForLevel = (level: string): string => {
  switch (level.toLowerCase()) {
    case 'ok':
      return 'rgba(46, 125, 50, 0.1)'
    case 'warning':
      return 'rgba(251, 192, 45, 0.1)'
    case 'error':
      return 'rgba(198, 40, 40, 0.1)'
    default:
      return 'rgba(46, 125, 50, 0.1)'
  }
}

const ResultCard = styled.div<{ level: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px;
  border-radius: ${theme.borderRadius.button};
  border: 1px solid ${(p) => getColorForLevel(p.level)};
  background: ${(p) => getBackgroundForLevel(p.level)};
`

const ResultMeta = styled.span`
  ${theme.text('small', 'content')};
  color: ${color('grayDark')};
`

const SuggestionList = styled.ul`
  margin: 0;
  padding-left: 20px;
`

const SuggestionItem = styled.li`
  ${theme.text('small', 'content')};
  color: ${color('grayDark')};
`

interface ValidationPanelProps {
  onValidationReadyChange?: (ready: boolean) => void
  onUnavailable: () => void
  validationReady?: boolean
}

const ValidationPanel = ({
  onValidationReadyChange,
  onUnavailable,
  validationReady = false,
}: ValidationPanelProps): JSX.Element => {
  const { t } = useTranslation()
  const { control } = useFormContext<CreateDefectFormData>()
  const defectGroups = useTypedSelector((state) => state.defect.defectGroups.data)

  const comment = useWatch({ control, name: 'comment' }) ?? ''
  const defectCode = useWatch({ control, name: 'defectCode' }) ?? ''
  const defectGroup = useWatch({ control, name: 'defectGroup' }) ?? ''
  const vehicleType = useWatch({ control, name: 'vehicleType' }) ?? ''
  // The form's installationGroup holds the equipment serial number (same value sent as
  // serialNumber by the comment/question calls).
  const serialNumber = useWatch({ control, name: 'installationGroup' }) ?? ''

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<DefectAssistantValidateResponse | null>(null)

  // Any change to a field that feeds /defectAssistant/validate invalidates a prior validation:
  // clear the local result and tell the parent it is no longer validated, so the send
  // button is gated behind a fresh validation.
  useEffect(() => {
    setResult(null)
    onValidationReadyChange?.(false)
  }, [defectCode, defectGroup, comment, vehicleType, serialNumber, onValidationReadyChange])

  const trimmedComment = comment.trim()
  const trimmedDefectCode = defectCode.trim()
  const trimmedDefectGroup = defectGroup.trim()
  const selectedDefectGroupDescription =
    defectGroups.find((group) => group.id === trimmedDefectGroup)?.description ?? trimmedDefectGroup

  const disabled = loading || trimmedComment.length === 0 || trimmedDefectCode.length === 0

  const handleClick = async () => {
    if (disabled) return

    setLoading(true)
    setResult(null)

    try {
      const response = await validateDefectInfo({
        description: trimmedComment,
        defectCode: trimmedDefectCode,
        defectGroup: selectedDefectGroupDescription,
        vehicleType,
        serialNumber,
      })
      setResult(response)
      onValidationReadyChange?.(true)
    } catch (err) {
      console.error('Failed to validate defect info:', err)
      onUnavailable()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container>
      {result && (
        <ResultCard level={result.severity}>
          {result.reasoning && <ResultMeta>{result.reasoning}</ResultMeta>}
          {result.suggestedFixes.length > 0 && (
            <SuggestionList>
              {result.suggestedFixes.map((fix, idx) => (
                <SuggestionItem key={`${fix}-${idx}`}>{fix}</SuggestionItem>
              ))}
            </SuggestionList>
          )}
        </ResultCard>
      )}
      {!validationReady && !result && (
        <AiButton type="button" onClick={handleClick} disabled={disabled}>
          {loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
          {loading ? t('defect.validate.checking') : t('defect.validate.validateAndSend')}
        </AiButton>
      )}
    </Container>
  )
}

export default ValidationPanel
